import { zodResolver } from '@hookform/resolvers/zod';
import { useFetcher } from '@remix-run/react';
import { platform } from '@todesktop/client-core';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { styled } from 'styled-components';
import { type z } from 'zod';
import { isClientError, isClientValidationError } from '@app/lib/HandleErrors.client';
import { FontSize } from '@app/themes/Fonts';
import Eye from '@icons/Eye.svg?react';
import { signupSchema, loginSchema } from '@shared/validators/Auth';
import GoogleLoginButton from './GoogleLoginButton';
import OutlookLoginButton from './OutlookLoginButton';

export enum AuthView {
  LOGIN = 'login',
  SIGNUP = 'signup',
  RESET_PASSWORD = 'reset-password',
}

interface AuthMainProps {
  defaultAuthView?: AuthView;
  invitationToken?: string | null;
}

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FormContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 336px;
`;

const Title = styled.h3`
  text-align: center;
  margin-top: 15px;
  margin-bottom: 25px;
`;

const InputWrapper = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-size: ${FontSize.normal};
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: ${FontSize.medium};
`;

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 336px;
  height: 48px;

  background-color: #241a17;
  color: white;
  border: 1px solid ${({ theme }) => theme.borderPrimary};
  border-radius: 6px;
  cursor: pointer;
`;

const ToggleLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const BottomLoginWrapper = styled.div`
  margin-top: 20px;
`;

const UnderneathText = styled.div`
  margin-top: 15px;
  text-align: center;
`;

const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
  margin-top: 5px;
  display: block;
`;

const ServerMessage = styled.div`
  color: green;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ theme }) => theme.textColorSecondary};
  font-size: 14px;
  margin: 20px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.borderPrimary};
  }

  &::before {
    margin-right: 10px;
  }

  &::after {
    margin-left: 10px;
  }
`;

const StyledEye = styled(Eye)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;

const AuthMain = ({ defaultAuthView = AuthView.SIGNUP, invitationToken = null }: AuthMainProps) => {
  const fetcher = useFetcher();
  const [authView, setAuthView] = useState<AuthView>(defaultAuthView);
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const schema = authView === AuthView.LOGIN ? loginSchema : signupSchema;
  type FormData = z.infer<typeof schema>;

  const generateAuthUrl = useCallback(
    (provider: string) => {
      const baseUrl = `/auth/${provider}/initiate`;
      const searchParams = new URLSearchParams();
      if (invitationToken) {
        searchParams.append('invitationToken', invitationToken);
      }
      if (platform.todesktop.isDesktopApp()) {
        searchParams.append('shouldGoBackToDesktop', 'true');
      }
      return `${baseUrl}?${searchParams}`;
    },
    [invitationToken],
  );

  // Initialize react-hook-form with Zod schema resolver
  const {
    register,
    formState: { errors },
    setError,
    setFocus,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  // TODO: improve typing
  const actionData: any = fetcher.data;
  useEffect(() => {
    if (!isClientError(actionData)) {
      if (actionData?.message) {
        setServerMessage(actionData.message);
      }
      return;
    }

    const handleErrors = () => {
      if (isClientValidationError(actionData)) {
        const { errorDetail } = actionData;

        let firstErrorFieldSet = false;
        for (const [fieldName, fieldErrors] of Object.entries(errorDetail)) {
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0 && fieldErrors[0]) {
            setError(fieldName as any, {
              type: 'manual',
              message: fieldErrors[0],
            });

            if (!firstErrorFieldSet) {
              setFocus(fieldName as any);
              firstErrorFieldSet = true;
            }
          }
        }
      }
    };

    handleErrors();
  }, [actionData, setError, setFocus]);

  return (
    <FormContainer>
      <fetcher.Form
        method='post'
        style={{ width: '100%' }}
        action={
          authView === AuthView.LOGIN
            ? '/auth/login'
            : authView === AuthView.SIGNUP
              ? '/auth/signup'
              : '/auth/reset-password'
        }
      >
        <Title>
          {authView === AuthView.LOGIN ? 'Login' : authView === AuthView.SIGNUP ? 'Sign Up' : 'Reset Password'}
        </Title>
        {authView !== AuthView.RESET_PASSWORD && (
          <>
            <ButtonWrapper>
              <GoogleLoginButton
                to={generateAuthUrl('google')}
                buttonText={authView === AuthView.SIGNUP ? 'Sign up with Google' : 'Sign in with Google'}
              />
              <OutlookLoginButton
                to={generateAuthUrl('outlook')}
                buttonText={authView === AuthView.SIGNUP ? 'Sign up with Outlook' : 'Sign in with Outlook'}
              />
            </ButtonWrapper>
            <Divider>or</Divider>
          </>
        )}

        {/* Hidden invitation token input for login and signup */}
        {(authView === AuthView.LOGIN || authView === AuthView.SIGNUP) && invitationToken && (
          <input type='hidden' name='invitationToken' value={invitationToken} />
        )}

        {/* Name input (only for signup) */}
        {authView === AuthView.SIGNUP && (
          <InputWrapper>
            <Label htmlFor='name'>Name</Label>
            <Input id='name' type='text' {...register('name')} placeholder='Enter your name' />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
          </InputWrapper>
        )}

        {/* Email input */}
        <InputWrapper>
          <Label htmlFor='email'>Email</Label>
          <Input id='email' type='email' {...register('email')} placeholder='Enter your email' />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
        </InputWrapper>

        {/* Password input (not for reset password) */}
        {authView !== AuthView.RESET_PASSWORD && (
          <InputWrapper>
            <Label htmlFor='password'>Password</Label>
            <InputWrapper style={{ position: 'relative' }}>
              <Input
                id='password'
                type={showPassword ? 'text' : 'password'}
                {...register('password')}
                placeholder='Enter your password'
              />
              <StyledEye onClick={() => setShowPassword(!showPassword)} />
            </InputWrapper>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </InputWrapper>
        )}

        {/* Server message */}
        {serverMessage && <ServerMessage>{serverMessage}</ServerMessage>}

        {/* Submit button */}
        <Button type='submit'>Continue</Button>

        {authView === AuthView.LOGIN && (
          <BottomLoginWrapper>
            <UnderneathText>
              Not a member? <ToggleLink onClick={() => setAuthView(AuthView.SIGNUP)}>Sign Up</ToggleLink>
            </UnderneathText>
            <UnderneathText>
              <ToggleLink onClick={() => setAuthView(AuthView.RESET_PASSWORD)}>Forgot password?</ToggleLink>
            </UnderneathText>
          </BottomLoginWrapper>
        )}
        {authView === AuthView.SIGNUP && (
          <UnderneathText>
            Already a member? <ToggleLink onClick={() => setAuthView(AuthView.LOGIN)}>Sign in</ToggleLink>
          </UnderneathText>
        )}
        {authView === AuthView.RESET_PASSWORD && (
          <UnderneathText>
            Remember your password? <ToggleLink onClick={() => setAuthView(AuthView.LOGIN)}>Login</ToggleLink>
          </UnderneathText>
        )}
      </fetcher.Form>
    </FormContainer>
  );
};

export default AuthMain;
